import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceIcon from "@mui/icons-material/Place";
import GroupsIcon from "@mui/icons-material/Groups";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import EmailIcon from "@mui/icons-material/Email";
import PaidIcon from "@mui/icons-material/Paid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QrCodeDialog from "../../components/QrCodeDialog";
import ParticipantInfoDialog from "../../components/ParticipantInfoDialog";
import DirectionsChip, { GOOGLE_MAPS_RED } from "../../components/DirectionsChip";
import AddressLink from "../../components/AddressLink";
import { getGame, updateRegistrationPaid, updateRegistrationRsvp } from "../../services/api/games";
import { formatGameDate, formatGameTime, formatCost, SKILL_LEVEL_LABELS } from "../../components/GameCard";
import { loadErrorMessage } from "../../utils/api-error";

const POSITION_LABELS = {
  CENTER: "C",
  WING: "W",
  DEFENSE: "D",
};

export default function GameRosterView() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [qrOpen, setQrOpen] = React.useState(false);
  const [togglingPaid, setTogglingPaid] = React.useState({});
  const [confirmUnpaid, setConfirmUnpaid] = React.useState({ open: false, registrationId: null });
  const [currentUserRsvp, setCurrentUserRsvp] = React.useState(null);
  const [currentUserRegistrationId, setCurrentUserRegistrationId] = React.useState(null);
  const [updatingRsvp, setUpdatingRsvp] = React.useState(false);
  const [paymentSummaryOpen, setPaymentSummaryOpen] = React.useState(false);
  const [selectedParticipant, setSelectedParticipant] = React.useState(null);
  const [copiedPaid, setCopiedPaid] = React.useState(false);
  const [copiedUnpaid, setCopiedUnpaid] = React.useState(false);
  const [showPaidEmails, setShowPaidEmails] = React.useState(false);
  const [showUnpaidEmails, setShowUnpaidEmails] = React.useState(false);

  const handleTogglePaid = async (registrationId, currentHasPaid) => {
    setTogglingPaid((prev) => ({ ...prev, [registrationId]: true }));
    try {
      await updateRegistrationPaid(registrationId, !currentHasPaid);
      setGame((prev) => {
        const patch = (list) =>
          list.map((r) => r.id === registrationId ? { ...r, hasPaid: !currentHasPaid } : r);
        return { ...prev, skaters: patch(prev.skaters), goalies: patch(prev.goalies) };
      });
    } finally {
      setTogglingPaid((prev) => ({ ...prev, [registrationId]: false }));
    }
  };

  const handleRsvpChange = async (_, newRsvp) => {
    if (!newRsvp || !currentUserRegistrationId) return;
    const prevRsvp = currentUserRsvp;
    setUpdatingRsvp(true);
    try {
      await updateRegistrationRsvp(currentUserRegistrationId, newRsvp);
      setCurrentUserRsvp(newRsvp);
      setGame((prev) => {
        if (!prev.rsvpCounts) return prev;
        const counts = { ...prev.rsvpCounts };
        if (prevRsvp === "YES") counts.yes = Math.max(0, counts.yes - 1);
        else if (prevRsvp === "NO") counts.no = Math.max(0, counts.no - 1);
        else counts.noResponse = Math.max(0, counts.noResponse - 1);
        if (newRsvp === "YES") counts.yes += 1;
        else if (newRsvp === "NO") counts.no += 1;
        else counts.noResponse += 1;
        return { ...prev, rsvpCounts: counts };
      });
    } finally {
      setUpdatingRsvp(false);
    }
  };

  React.useEffect(() => {
    if (!gameId) return;
    getGame(gameId)
      .then((g) => {
        setGame(g);
        setCurrentUserRsvp(g.currentUserRsvp ?? "NO_RESPONSE");
        setCurrentUserRegistrationId(g.currentUserRegistrationId ?? null);
      })
      .catch((err) => setError(loadErrorMessage(err, "Failed to load game.")))
      .finally(() => setLoading(false));
  }, [gameId]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  if (error || !game) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error || "Game not found."}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Container>
    );
  }

  const maxSkater = game.maxSkaterSpots ?? 0;
  const maxGoalie = game.maxGoalieSpots ?? 0;
  const skatersFilled = game.skaterSpotsFilled ?? 0;
  const goaliesFilled = game.goalieSpotsFilled ?? 0;
  const skatersAvailable = Math.max(0, maxSkater - skatersFilled);
  const goaliesAvailable = Math.max(0, maxGoalie - goaliesFilled);
const cityState =
    game.league?.city && game.league?.state
      ? `${game.league.city}, ${game.league.state}`
      : "";
  const locationAddress = game.rink?.address;
  const skillLabel = SKILL_LEVEL_LABELS[game.skillLevel] ?? game.skillLevel ?? "—";
  const playerCost = formatCost(game.league?.playerCost);
  const goalieCost = formatCost(game.league?.goalieCost);

  const signupUrl = game.league?.id
    ? `${window.location.origin}/leagues/${game.league.id}/signup`
    : null;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {signupUrl && (
        <QrCodeDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          title={game.league?.name}
          subtitle="Scan to join this league"
          url={signupUrl}
        />
      )}

      <Stack spacing={3}>
        <Stack spacing={1}>
          <Button
            variant="text"
            onClick={() => navigate(-1)}
            sx={{ pl: 0, alignSelf: "flex-start" }}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          {(signupUrl || (game.currentUserIsRegistered && game.league?.superAdmin?.email) || game.currentUserIsAdmin) && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {signupUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href={signupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  League signup
                </Button>
              )}
              {game.currentUserIsRegistered && game.league?.superAdmin?.email && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EmailIcon />}
                  component="a"
                  href={`mailto:${game.league.superAdmin.email}?subject=${encodeURIComponent(`Inquiry - ${game.league.name}`)}`}
                >
                  Contact organizer
                </Button>
              )}
              {signupUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<QrCode2Icon />}
                  onClick={() => setQrOpen(true)}
                >
                  Share
                </Button>
              )}
              {game.currentUserIsAdmin && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PaidIcon />}
                  onClick={() => { setShowPaidEmails(false); setShowUnpaidEmails(false); setCopiedPaid(false); setCopiedUnpaid(false); setPaymentSummaryOpen(true); }}
                >
                  Payment summary
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <Box>
          <Typography variant="h4" component="h3">
            {game.arenaName || game.league?.name}
          </Typography>
          {game.arenaName && game.league?.name && (
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {game.league.name}
            </Typography>
          )}

          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarMonthIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatGameDate(game.startsAt)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatGameTime(game.startsAt)}
                {game.durationMinutes ? ` · ${game.durationMinutes} min` : ""}
              </Typography>
            </Stack>
            {(locationAddress || cityState) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <PlaceIcon fontSize="small" sx={{ color: GOOGLE_MAPS_RED }} />
                <Typography variant="body2" color="text.secondary">
                  {/* The address opens Google Maps directions, the same page as the Directions chip below. */}
                  <AddressLink address={locationAddress}>
                    {locationAddress
                      ? `${locationAddress}${cityState ? ` · ${cityState}` : ""}`
                      : cityState}
                  </AddressLink>
                </Typography>
              </Stack>
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Chip label={skillLabel} size="small" variant="outlined" />
            <Chip label={`Skaters: ${playerCost}`} size="small" variant="outlined" />
            {goalieCost === "Free" ? (
              <Chip label="Goalies: Free" size="small" variant="outlined" />
            ) : (
              <Chip label={`Goalies: ${goalieCost}`} size="small" variant="outlined" />
            )}
            <DirectionsChip address={locationAddress} />
          </Stack>
        </Box>

        {game.status === "CANCELLED" && (
          <Alert
            severity="error"
            style={{ backgroundColor: "rgba(140, 20, 20, 0.7)", color: "white" }}
            sx={{ fontWeight: 700, "& .MuiAlert-icon": { color: "white" } }}
          >
            This game has been cancelled.
          </Alert>
        )}

        <Divider />

        {game.currentUserIsRegistered && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Your RSVP
            </Typography>
            <ToggleButtonGroup
              value={currentUserRsvp}
              exclusive
              onChange={handleRsvpChange}
              disabled={updatingRsvp}
              size="small"
            >
              <ToggleButton value="YES" color="success">Going</ToggleButton>
              <ToggleButton value="NO" color="error">Can&apos;t make it</ToggleButton>
              <ToggleButton value="NO_RESPONSE">No response</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {game.currentUserIsAdmin && game.rsvpCounts && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              RSVP Summary
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Going: ${game.rsvpCounts.yes}`} size="small" color="success" variant="outlined" />
              <Chip label={`Not going: ${game.rsvpCounts.no}`} size="small" color="error" variant="outlined" />
              <Chip label={`No response: ${game.rsvpCounts.noResponse}`} size="small" variant="outlined" />
            </Stack>
          </Box>
        )}

        {maxSkater > 0 && (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <GroupsIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight={600}>
                Skaters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({skatersFilled} / {maxSkater})
              </Typography>
              {skatersAvailable === 0 ? (
                <Chip label="Full" color="error" size="small" />
              ) : (
                <Chip label={`${skatersAvailable} open`} color="success" size="small" />
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, maxSkater > 0 ? (skatersFilled / maxSkater) * 100 : 0)}
              color={skatersAvailable === 0 ? "error" : "primary"}
              sx={{ mb: 1, height: 6, borderRadius: 1 }}
            />
            {game.currentUserIsAdmin ? (
              game.skaters?.length > 0 ? (
                <List dense disablePadding>
                  {game.skaters.map((s, i) => (
                    <ListItem key={s.id} disableGutters disablePadding>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography
                              variant="body2"
                              onClick={() => setSelectedParticipant({ firstName: s.firstName, lastName: s.lastName, email: s.email, phone: s.phone ?? null, hasOutstandingPayments: !s.hasPaid })}
                              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline", color: "primary.main" } }}
                            >
                              {i + 1}.{" "}{s.firstName} {s.lastName}
                            </Typography>
                            {s.preferredPosition && (
                              <Chip label={POSITION_LABELS[s.preferredPosition] ?? s.preferredPosition} size="small" sx={{ height: 18, fontSize: "0.7rem" }} />
                            )}
                            {s.hasPaid !== undefined && (
                              <Chip
                                label={s.hasPaid ? "Paid" : "Unpaid"}
                                size="small"
                                color={s.hasPaid ? "success" : "warning"}
                                variant="outlined"
                              />
                            )}
                            {s.rsvp && (
                              <Chip
                                label={s.rsvp === "YES" ? "Going" : s.rsvp === "NO" ? "Not going" : "No response"}
                                size="small"
                                color={s.rsvp === "YES" ? "success" : s.rsvp === "NO" ? "error" : "default"}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        }
                      />
                      <Button
                        size="small"
                        variant="text"
                        color={s.hasPaid ? "warning" : "success"}
                        disabled={!!togglingPaid[s.id]}
                        onClick={() => s.hasPaid ? setConfirmUnpaid({ open: true, registrationId: s.id }) : handleTogglePaid(s.id, false)}
                        sx={{ flexShrink: 0, minWidth: 80 }}
                      >
                        {togglingPaid[s.id] ? "…" : s.hasPaid ? "Mark unpaid" : "Mark paid"}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No skaters yet. Be first on the ice!
                </Typography>
              )
            ) : (
              game.skaters?.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Lineup / Position Stats:</Typography>
                  {[
                    { label: "Center", count: game.skaters.filter((s) => s.preferredPosition === "CENTER").length },
                    { label: "Wing", count: game.skaters.filter((s) => s.preferredPosition === "WING").length },
                    { label: "Defense", count: game.skaters.filter((s) => s.preferredPosition === "DEFENSE").length },
                    { label: "Not Specified", count: game.skaters.filter((s) => !s.preferredPosition).length },
                  ]
                    .filter((p) => p.count > 0)
                    .map((p) => (
                      <Chip key={p.label} label={`${p.label}: ${p.count}`} size="small" variant="outlined" />
                    ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No skaters yet. Be first on the ice!
                </Typography>
              )
            )}
          </Box>
        )}

        {maxGoalie > 0 && (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <SportsHockeyIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight={600}>
                Goalies
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({goaliesFilled} / {maxGoalie})
              </Typography>
              {goaliesAvailable === 0 ? (
                <Chip label="Full" color="error" size="small" />
              ) : (
                <Chip label={`${goaliesAvailable} open`} color="warning" size="small" />
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, maxGoalie > 0 ? (goaliesFilled / maxGoalie) * 100 : 0)}
              color={goaliesAvailable === 0 ? "error" : "warning"}
              sx={{ mb: 1, height: 6, borderRadius: 1 }}
            />
            {game.currentUserIsAdmin ? (
              game.goalies?.length > 0 ? (
                <List dense disablePadding>
                  {game.goalies.map((g, i) => (
                    <ListItem key={g.id} disableGutters disablePadding>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography
                              variant="body2"
                              onClick={() => setSelectedParticipant({ firstName: g.firstName, lastName: g.lastName, email: g.email, phone: g.phone ?? null, hasOutstandingPayments: !g.hasPaid })}
                              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline", color: "primary.main" } }}
                            >
                              {i + 1}.{" "}{g.firstName} {g.lastName}
                            </Typography>
                            {g.hasPaid !== undefined && (
                              <Chip
                                label={g.hasPaid ? "Paid" : "Unpaid"}
                                size="small"
                                color={g.hasPaid ? "success" : "warning"}
                                variant="outlined"
                              />
                            )}
                            {g.rsvp && (
                              <Chip
                                label={g.rsvp === "YES" ? "Going" : g.rsvp === "NO" ? "Not going" : "No response"}
                                size="small"
                                color={g.rsvp === "YES" ? "success" : g.rsvp === "NO" ? "error" : "default"}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        }
                      />
                      <Button
                        size="small"
                        variant="text"
                        color={g.hasPaid ? "warning" : "success"}
                        disabled={!!togglingPaid[g.id]}
                        onClick={() => g.hasPaid ? setConfirmUnpaid({ open: true, registrationId: g.id }) : handleTogglePaid(g.id, false)}
                        sx={{ flexShrink: 0, minWidth: 80 }}
                      >
                        {togglingPaid[g.id] ? "…" : g.hasPaid ? "Mark unpaid" : "Mark paid"}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No goalies yet — pads welcome!
                </Typography>
              )
            ) : (
              game.goalies?.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip label={`Goalie: ${game.goalies.length}`} size="small" variant="outlined" />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No goalies yet — pads welcome!
                </Typography>
              )
            )}
          </Box>
        )}

      </Stack>

      <Dialog open={confirmUnpaid.open} onClose={() => setConfirmUnpaid({ open: false, registrationId: null })}>
        <DialogTitle>Mark as unpaid?</DialogTitle>
        <DialogContent>
          <DialogContentText>This will remove the paid status for this participant. Are you sure?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnpaid({ open: false, registrationId: null })}>Cancel</Button>
          <Button
            color="warning"
            onClick={() => {
              handleTogglePaid(confirmUnpaid.registrationId, true);
              setConfirmUnpaid({ open: false, registrationId: null });
            }}
          >
            Mark unpaid
          </Button>
        </DialogActions>
      </Dialog>

      <ParticipantInfoDialog
        open={!!selectedParticipant}
        onClose={() => setSelectedParticipant(null)}
        participant={selectedParticipant}
      />

      {/* Payment summary dialog — owner/admin only */}
      <Dialog open={paymentSummaryOpen} onClose={() => setPaymentSummaryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Payment summary</DialogTitle>
        <DialogContent>
          {(() => {
            const allRegistrants = [...(game?.skaters ?? []), ...(game?.goalies ?? [])];
            const paid = allRegistrants.filter((r) => r.hasPaid);
            const unpaid = allRegistrants.filter((r) => !r.hasPaid);
            const paidEmails = paid.map((r) => r.email).filter(Boolean);
            const unpaidEmails = unpaid.map((r) => r.email).filter(Boolean);
            const paidEmailStr = paidEmails.map((e) => `${e};`).join("\n");
            const unpaidEmailStr = unpaidEmails.map((e) => `${e};`).join("\n");

            const copyToClipboard = (text, setCopied) => {
              navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            };

            return (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
                {/* Paid column */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="success.main">
                      Paid ({paid.length})
                    </Typography>
                    <Tooltip title={copiedPaid ? "Copied!" : "Copy email list"}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={copiedPaid ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
                          disabled={paidEmails.length === 0}
                          onClick={() => { setShowPaidEmails(true); copyToClipboard(paidEmailStr, setCopiedPaid); }}
                        >
                          {copiedPaid ? "Copied!" : "Copy emails"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                  {paid.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No paid participants yet.</Typography>
                  ) : (
                    <List dense disablePadding>
                      {paid.map((r) => (
                        <ListItem key={r.id} disableGutters disablePadding>
                          <ListItemText
                            primary={`${r.firstName} ${r.lastName}`}
                            secondary={r.email}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {showPaidEmails && paidEmails.length > 0 && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Email list (semicolon-separated):</Typography>
                      <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "monospace" }}>
                        {paidEmailStr}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                <Divider sx={{ display: { xs: "block", sm: "none" } }} />

                {/* Unpaid column */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="warning.main">
                      Unpaid ({unpaid.length})
                    </Typography>
                    <Tooltip title={copiedUnpaid ? "Copied!" : "Copy email list"}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={copiedUnpaid ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
                          disabled={unpaidEmails.length === 0}
                          onClick={() => { setShowUnpaidEmails(true); copyToClipboard(unpaidEmailStr, setCopiedUnpaid); }}
                        >
                          {copiedUnpaid ? "Copied!" : "Copy emails"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                  {unpaid.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Everyone has paid!</Typography>
                  ) : (
                    <List dense disablePadding>
                      {unpaid.map((r) => (
                        <ListItem key={r.id} disableGutters disablePadding>
                          <ListItemText
                            primary={`${r.firstName} ${r.lastName}`}
                            secondary={r.email}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {showUnpaidEmails && unpaidEmails.length > 0 && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Email list (semicolon-separated):</Typography>
                      <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "monospace" }}>
                        {unpaidEmailStr}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}
